import { z } from 'zod';
import {
  createAIProvider,
  getProviderApiKey,
  isValidProvider,
} from 'data/conversational-search/providers';
import { buildSmartSearchSystemPrompt } from './system-prompt-builder';
import { smartSearchLlmResponseSchema } from './schema';
import { isSmartSearchTag } from './smart-search-tags';
import type {
  ParsedSearchFilters,
  SmartSearchChatClient,
  SmartSearchLlmResponse,
} from './types';

const LlmJsonEnvelopeSchema = z.object({ tags: z.array(z.unknown()).optional() }).passthrough();

/** Keep canonical tags; drop paraphrases so one bad string does not reject the whole response. */
function keepCanonicalTags(raw: unknown): unknown {
  const envelope = LlmJsonEnvelopeSchema.safeParse(raw);
  if (!envelope.success) {
    return raw;
  }
  const tags = (envelope.data.tags ?? []).filter(
    (tag): tag is string => typeof tag === 'string' && isSmartSearchTag(tag)
  );
  return { ...envelope.data, tags };
}

export interface InferFiltersWithLlmInput {
  query: string;
  residual: string | null;
  existing: ParsedSearchFilters;
  generateTags: boolean;
  now: Date;
  provider: SmartSearchChatClient;
}

/** Deterministic values win; LLM only fills empty keys. */
export function mergeFiltersFillGaps(
  base: ParsedSearchFilters,
  extra: ParsedSearchFilters
): ParsedSearchFilters {
  const merged: ParsedSearchFilters = { ...extra, ...base };
  if (base.homeType) {
    merged.homeType = base.homeType;
  }
  const extraCondo = extra.additional?.condoOrTownhouse;
  const baseCondo = base.additional?.condoOrTownhouse;
  if (extraCondo || baseCondo) {
    merged.additional = {
      condoOrTownhouse: { ...extraCondo, ...baseCondo },
    };
  }
  return merged;
}

/** Env-backed OpenAI/Google client, or null if it cannot be created. */
export function createDefaultSmartSearchAiProvider(): SmartSearchChatClient | null {
  const envProvider = process.env.AI_PROVIDER;
  const providerType = envProvider && isValidProvider(envProvider) ? envProvider : 'openai';
  const apiKey = getProviderApiKey(providerType);
  if (!apiKey) return null;

  const temperature = Number.parseFloat(process.env.AI_TEMPERATURE || '0.1');
  const maxTokens = Number.parseInt(process.env.AI_MAX_TOKENS || '1000', 10);

  try {
    return createAIProvider(providerType, apiKey, {
      model: process.env.AI_MODEL || 'gpt-4o-mini',
      temperature: Number.isFinite(temperature) ? temperature : 0.1,
      maxTokens: Number.isFinite(maxTokens) ? maxTokens : 1000,
    });
  } catch (error) {
    // The SDK client refuses to construct in some environments — e.g. a
    // browser-like context (jsdom, or the client bundle) without
    // `dangerouslyAllowBrowser`. Treat that like a missing key: skip the LLM
    // step and fall back to the deterministic parse instead of crashing the
    // whole parse, mirroring how `inferFiltersWithLlm` already swallows call
    // failures.
    console.error('createDefaultSmartSearchAiProvider: failed to construct AI client', error);
    return null;
  }
}

export async function inferFiltersWithLlm(
  input: InferFiltersWithLlmInput
): Promise<SmartSearchLlmResponse | null> {
  let userContent = `USER QUERY:\n${input.query}`;
  if (Object.keys(input.existing).length > 0) {
    userContent += `\n\nALREADY EXTRACTED (ground truth — fill gaps only, do not override):\n${JSON.stringify(input.existing, null, 2)}`;
  }
  if (input.residual) {
    userContent += `\n\nRESIDUAL (not yet parsed):\n${input.residual}`;
  }
  if (input.generateTags) {
    userContent +=
      '\n\nWalk the entire allowed tag list. Infer related groups of tags from the USER QUERY yourself (typical amenities, synonyms, implications) — not only tags that share words with the query. Output the canonical tag strings.';
  }

  try {
    const response = await input.provider.chat(
      [
        {
          role: 'system',
          content: buildSmartSearchSystemPrompt({
            generateTags: input.generateTags,
            currentDate: input.now.toISOString().split('T')[0],
          }),
        },
        { role: 'user', content: userContent },
      ],
      { jsonMode: true }
    );

    const raw: unknown = JSON.parse(
      response.content.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '')
    );
    const parsed = smartSearchLlmResponseSchema(input.generateTags).safeParse(
      input.generateTags ? keepCanonicalTags(raw) : raw
    );
    if (!parsed.success) {
      console.error('inferFiltersWithLlm: invalid LLM JSON', parsed.error);
      return null;
    }

    const result: SmartSearchLlmResponse = {
      filters: parsed.data.filters,
      confidence: parsed.data.confidence,
      unparsed: parsed.data.unparsed ?? null,
    };
    if ('tags' in parsed.data && Array.isArray(parsed.data.tags)) {
      result.tags = parsed.data.tags.filter((tag): tag is string => typeof tag === 'string');
    }
    return result;
  } catch (error) {
    console.error('inferFiltersWithLlm: LLM call failed', error);
    return null;
  }
}
